// ---------------------------------------------------------------------------
// Digital Notice Board — data access layer (offline-first)
// ---------------------------------------------------------------------------
// Boards are persisted to localStorage so the studio is fully usable before the
// backend exists. Every function returns the normalized { data, source } shape
// used across the app; `source` is "local" today and becomes "api" once the
// noticeBoardApiConstants endpoints are wired in (mirrors AttendanceService).
// ---------------------------------------------------------------------------
import {STORAGE_KEYS} from "../constants/NoticeBoardConstants";
import {buildSampleBoard} from "../data/noticeBoardMockData";

const ok = (data, source = "local") => ({data, source});

function readAll() {
    try {
        const raw = localStorage.getItem(STORAGE_KEYS.BOARDS);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : null;
    } catch (e) {
        console.warn("[NoticeBoardService] corrupt store, resetting:", e?.message);
        return null;
    }
}

function writeAll(boards) {
    localStorage.setItem(STORAGE_KEYS.BOARDS, JSON.stringify(boards));
}

// Seeds a friendly sample on first ever load so the list is never empty.
export function listBoards() {
    let boards = readAll();
    if (boards === null) {
        boards = [buildSampleBoard()];
        writeAll(boards);
    }
    // Newest first.
    const sorted = boards.slice().sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
    return ok(sorted);
}

export function getBoard(id) {
    const boards = readAll() || [];
    return ok(boards.find((b) => b.id === id) || null);
}

// Upsert. Stamps updatedAt so the list stays ordered by recency.
export function saveBoard(board) {
    const boards = readAll() || [];
    const stamped = {...board, updatedAt: Date.now()};
    const idx = boards.findIndex((b) => b.id === board.id);
    if (idx === -1) boards.push(stamped);
    else boards[idx] = stamped;
    writeAll(boards);
    return ok(stamped);
}

export function deleteBoard(id) {
    const boards = (readAll() || []).filter((b) => b.id !== id);
    writeAll(boards);
    return ok({id, deleted: true});
}

export function duplicateBoard(id) {
    const boards = readAll() || [];
    const src = boards.find((b) => b.id === id);
    if (!src) return ok(null);
    const copy = JSON.parse(JSON.stringify(src));
    copy.id = `board_${Date.now().toString(36)}`;
    copy.name = `${src.name} (copy)`;
    copy.updatedAt = Date.now();
    boards.push(copy);
    writeAll(boards);
    return ok(copy);
}
